import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from '@remix-run/react';
import cn from 'classnames';

import { COMMUNITY_ROUTES } from '~/constants/community';
import type { CommunitySearchResults } from '~/interfaces/community';

import type { useCommunityTrendingPosts } from '../../_hooks/useCommunitySearch';
import { CommentResultRow } from './_components/CommentResultRow';
import { SkeletonRow } from './_components/SkeletonRow';
import { MobileMemberResultRow } from './MobileMemberResultRow';
import { PostResultRow } from './PostResultRow';

type TabKey = 'posts' | 'comments' | 'members';

interface MobileSearchSheetProps {
  query: string;
  onQueryChange: (value: string) => void;
  onClose: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  results: CommunitySearchResults | undefined;
  isSearching: boolean;
  trendingPosts: ReturnType<typeof useCommunityTrendingPosts>;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export const MobileSearchSheet: FC<MobileSearchSheetProps> = ({
  query,
  onQueryChange,
  onClose,
  onKeyDown,
  results,
  isSearching,
  trendingPosts,
  inputRef,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('posts');

  // Guarantee auto-focus on open per AC ("Input auto-focused on open")
  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const hasQuery = query.trim().length > 0;

  // Reset to Posts when the query is cleared so a fresh search starts on the default tab.
  // Mid-typing refinements preserve the user's selected tab.
  useEffect(() => {
    if (!hasQuery) {
      setActiveTab('posts');
    }
  }, [hasQuery]);
  const hasResults =
    !!results && (results.posts.length > 0 || results.comments.length > 0 || results.members.length > 0);

  const tabCounts: Record<TabKey, number> = {
    posts: results?.total_counts?.posts ?? results?.posts.length ?? 0,
    comments: results?.total_counts?.comments ?? results?.comments.length ?? 0,
    members: results?.total_counts?.members ?? results?.members.length ?? 0,
  };

  const tabs: TabKey[] = ['posts', 'comments', 'members'];

  return (
    <>
      {/* Search header */}
      <div className="flex items-center gap-2 shrink-0 py-2 pr-2 pl-3 border-b border-black/10">
        <div className="flex items-center justify-center size-8 shrink-0">
          <MagnifyingGlassIcon className="size-4 text-black" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label={t('community.search.placeholder')}
          placeholder={t('community.search.placeholder')}
          className="flex-1 outline-none ring-0 focus:outline-none focus:ring-0 bg-transparent border-none p-0 text-base leading-5 tracking-[-0.01em] text-black"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label={t('community.search.close')}
          className="flex items-center justify-center size-10 shrink-0"
        >
          <XMarkIcon className="size-6 text-black opacity-50" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {/* A. Empty state — trending posts */}
        {!hasQuery && !isSearching && (
          <div className="flex flex-col">
            <div className="px-4 py-2">
              <span className="font-semibold opacity-50 text-[11px] leading-none text-black">
                {t('community.search.trending_posts')}
              </span>
            </div>
            {trendingPosts.isLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list
                  <SkeletonRow key={`trending-skeleton-${i}`} />
                ))
              : trendingPosts.data?.posts.map((post) => <PostResultRow key={post.id} post={post} onClick={onClose} />)}
          </div>
        )}

        {/* B. Searching */}
        {isSearching && (
          <div className="flex flex-col">
            {Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list
              <SkeletonRow key={`skeleton-${i}`} />
            ))}
          </div>
        )}

        {/* C. Results */}
        {!isSearching && hasQuery && hasResults && results && (
          <div className="flex flex-col">
            <h2 className="px-4 pb-2 pt-2 text-[17px] leading-6 font-semibold tracking-[-0.02em] text-black">
              {t('community.search.results_heading', {
                query,
                interpolation: { escapeValue: false },
              })}
            </h2>

            {/* Tab bar */}
            <div role="tablist" className="flex items-center gap-6 h-9 pl-4 border-b border-black/10">
              {tabs.map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`mobile-tabpanel-${tab}`}
                    id={`mobile-tab-${tab}`}
                    onClick={() => setActiveTab(tab)}
                    className={cn('flex items-center gap-[3px] whitespace-nowrap relative py-2 h-9', {
                      'border-b border-black/80': isActive,
                    })}
                  >
                    <span
                      className={cn('text-[15px] leading-5 tracking-[-0.01em]', {
                        'font-semibold text-black': isActive,
                        'font-medium text-black/50': !isActive,
                      })}
                    >
                      {t(`community.search.${tab}`)}
                    </span>
                    {tabCounts[tab] > 0 && (
                      <span className="text-[15px] leading-5 font-normal tracking-[-0.01em] text-black">
                        {tabCounts[tab]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab panel */}
            <div
              role="tabpanel"
              id={`mobile-tabpanel-${activeTab}`}
              aria-labelledby={`mobile-tab-${activeTab}`}
              className="flex flex-col"
            >
              {activeTab === 'posts' &&
                (results.posts.length > 0 ? (
                  results.posts.map((post) => <PostResultRow key={post.id} post={post} onClick={onClose} />)
                ) : (
                  <p className="px-4 py-8 text-center text-sm text-black/50">{t('community.search.no_results')}</p>
                ))}

              {activeTab === 'comments' &&
                (results.comments.length > 0 ? (
                  results.comments.map((comment) => (
                    <Link
                      key={comment.id}
                      to={`${COMMUNITY_ROUTES.root}/posts/${comment.post.id}#${comment.id}`}
                      onClick={onClose}
                    >
                      <CommentResultRow comment={comment} />
                    </Link>
                  ))
                ) : (
                  <p className="px-4 py-8 text-center text-sm text-black/50">{t('community.search.no_results')}</p>
                ))}

              {activeTab === 'members' &&
                (results.members.length > 0 ? (
                  results.members.map((member) => (
                    <MobileMemberResultRow key={member.id} member={member} onClose={onClose} />
                  ))
                ) : (
                  <p className="px-4 py-8 text-center text-sm text-black/50">{t('community.search.no_results')}</p>
                ))}
            </div>
          </div>
        )}

        {/* D. No results */}
        {!isSearching && hasQuery && !hasResults && (
          <p className="px-4 py-8 text-center text-sm text-black/50">{t('community.search.no_results')}</p>
        )}
      </div>
    </>
  );
};
